/**
 * Copy / Message Suggester modal.
 *
 * Usage in Template Editor:
 *   const [open, setOpen] = useState(false);
 *   <Button onClick={() => setOpen(true)} leftIcon={<span>✨</span>}>Suggest Variants (AI)</Button>
 *   <CopySuggesterModal isOpen={open} onClose={() => setOpen(false)}
 *      defaults={{ brief: formData.masterMessageContent, channel: "sms", variables: ["name"] }}
 *      onUseVariant={(text) => updateField("smsBody", text)} />
 *
 * Local-only.
 */
import React, { useState } from "react";
import {
  Box, Text, Button, Input, Select, HStack, VStack, Tag, TagLabel, Code,
  Flex, Badge, FormControl, FormLabel, Divider, useToast, Grid, GridItem
} from "@chakra-ui/react";
import { LuMessageSquare, LuExternalLink, LuPhone, LuReply, LuCopy } from "react-icons/lu";
import AIAgentModal from "./AIAgentModal";

// Channels exposed by the agent. MUST stay in sync with CHANNEL_LIMITS in
// xnotify-aiagents/copy-suggester/lib/config.mjs and with the Create
// Template page's channel tabs (ChannelTabsFixed.jsx). If the operator
// can pick a channel here, the agent has a body-length rule + prompt
// guidance for it.
// "all" is a synthetic option — gateway fans out, drafting variants
// per channel and returning a grouped result.
const CHANNELS = [
  { value: "all",      label: "All Channels (generate for each)" },
  { value: "sms",      label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "rcs",      label: "RCS" },
  { value: "email",    label: "Email" },
  { value: "push",     label: "Push" },
  { value: "voice",    label: "Voice (IVR)" },
  { value: "inapp",    label: "In-App" },
];
const TONES = ["brand", "casual", "formal", "urgent"];
// WhatsApp template categories (AUTHENTICATION / UTILITY / MARKETING) plus
// a generic "informational" bucket for the non-WA channels. The agent
// uses this to shape the variant — e.g. AUTHENTICATION must NOT carry
// marketing language, UTILITY must be transactional-style, MARKETING
// can include offers / CTAs etc.
const MESSAGE_TYPES = [
  { value: "authentication",          label: "Authentication (OTP, login codes)" },
  { value: "transactional",           label: "Transactional / Utility" },
  { value: "marketing",               label: "Marketing" },
  { value: "informational",           label: "Informational" },
];
const VERDICT_COLOR = { clean: "green", warnings_only: "yellow", blocked: "red" };

export default function CopySuggesterModal({ isOpen, onClose, defaults = {}, onUseVariant, openerChannel }) {
  // `openerChannel` — the channel of the field that opened this modal
  // (e.g. "whatsapp" when the SMS-tab WhatsApp editor opened it). Used
  // to gate "Use this" in All-Channels mode so we don't paste a Voice
  // script into the WhatsApp body. Falls back to defaults.channel when
  // not provided.
  const [brief,        setBrief]        = useState(defaults.brief || "");
  const [channel,      setChannel]      = useState(defaults.channel || "sms");
  const [tone,         setTone]         = useState(defaults.tone || "brand");
  const [count,        setCount]        = useState(defaults.variants_count || 3);
  const [messageType,  setMessageType]  = useState(defaults.message_type || "marketing");
  const [variables,    setVariables]    = useState(
    Array.isArray(defaults.variables) ? defaults.variables.join(",") : (defaults.variables || "")
  );

  const formContent = (
    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
      <GridItem colSpan={2}>
        <FormControl isRequired>
          <FormLabel fontSize="xs" mb={1}>Brief</FormLabel>
          <Input size="sm" value={brief} onChange={(e) => setBrief(e.target.value)}
                 placeholder="e.g. welcome new signup, drive first purchase" />
        </FormControl>
      </GridItem>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Channel</FormLabel>
        <Select size="sm" value={channel} onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Message Type</FormLabel>
        <Select size="sm" value={messageType} onChange={(e) => setMessageType(e.target.value)}>
          {MESSAGE_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Tone</FormLabel>
        <Select size="sm" value={tone} onChange={(e) => setTone(e.target.value)}>
          {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Variants</FormLabel>
        <Input size="sm" type="number" min={1} max={6} value={count} onChange={(e) => setCount(e.target.value)} />
      </FormControl>
      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Allowed variables (csv)</FormLabel>
          <Input size="sm" value={variables} onChange={(e) => setVariables(e.target.value)}
                 placeholder="first_name, order_id, discount_percent" />
          <Text fontSize="11px" color="gray.500" mt={1}>
            Bare names, comma-separated. The agent renders each as <Code fontSize="11px">$name</Code> in the variant (e.g. <Code fontSize="11px">first_name</Code> → <Code fontSize="11px">$first_name</Code>). Leading <Code fontSize="11px">$</Code> is accepted and stripped.
          </Text>
        </FormControl>
      </GridItem>
    </Grid>
  );

  return (
    <AIAgentModal
      isOpen={isOpen}
      onClose={onClose}
      agentId="copy-suggester"
      title="Suggest Message Variants (AI)"
      description="Channel-aware drafts grounded in your brand voice. Validated before output."
      icon={<Box w={9} h={9} borderRadius="lg" bg="blue.50" display="flex" alignItems="center" justifyContent="center"><LuMessageSquare color="#3B82F6" size={18} /></Box>}
      autoRun={false}
      ctaLabel="Generate variants"
      formContent={brief.trim() ? formContent : <Box>{formContent}<Text mt={2} fontSize="xs" color="red.500">Brief is required.</Text></Box>}
      fields={{
        brief, channel, tone, variants: Number(count),
        message_type: messageType,
        variables: variables.trim() || undefined
      }}
      renderResult={(r, ctx) => <Result result={r} onUseVariant={onUseVariant} onClose={onClose}
                                    openerChannel={openerChannel || defaults.channel || channel}
                                    rerun={ctx?.rerun} />}
    />
  );
}

/**
 * DA-BUG-0266 — when the agent finishes with zero variants, the modal
 * used to silently render "No variants generated.". Operators had no way
 * to tell whether: the LLM returned an empty list, the validator blocked
 * every draft, the brief was rejected, OPENAI_API_KEY was missing,
 * brand-voice discovery errored, or "all channels" returned 0 across the
 * board. This component answers "Why?" using the envelope's own
 * diagnostic fields.
 */
function EmptyVariantsExplanation({ result }) {
  const r = result || {};
  const reasons = [];

  // 1. Multi-channel: surface per-channel error / count breakdown.
  if (r.by_channel && typeof r.by_channel === "object") {
    const buckets = Object.entries(r.by_channel);
    const failed  = buckets.filter(([, b]) => !b?.ok);
    const emptyOk = buckets.filter(([, b]) => b?.ok && !(b?.result?.variants?.length));
    if (failed.length) {
      reasons.push({
        title: "Per-channel errors",
        lines: failed.map(([ch, b]) => `${ch}: ${b?.error || `agent exited ${b?.exit_code ?? "?"}`}`),
      });
    }
    if (emptyOk.length) {
      reasons.push({
        title: "Channels that succeeded but produced 0 variants",
        lines: emptyOk.map(([ch]) => ch),
      });
    }
  }

  // 2. Discovery errors from the agent (MCP / tenant.profile / brand-voice fetch).
  if (Array.isArray(r.discovery_errors) && r.discovery_errors.length) {
    reasons.push({
      title: "Discovery errors",
      lines: r.discovery_errors.map((e) => (typeof e === "string" ? e : (e?.message || JSON.stringify(e)))),
    });
  }

  // 3. Validator blocked everything (errors === total in summary).
  const summary = r.summary || {};
  if (summary.blocked > 0 && (summary.clean === 0) && (summary.warnings_only === 0)) {
    reasons.push({
      title: "All drafts blocked by the validator",
      lines: [`${summary.blocked} draft(s) failed channel / variable / WhatsApp rules and were filtered out.`],
    });
  }

  // 4. Top-level error surface (gateway / agent crash).
  if (r.error) {
    reasons.push({ title: "Agent error", lines: [r.error] });
  }

  // 5. requires_onboarding shouldn't reach here (handled above), but defend.
  if (r.requires_onboarding) {
    reasons.push({
      title: "Brand voice not configured",
      lines: ["Onboarding required — no approved templates and no voice profile for this tenant. Complete the brand-voice questionnaire and re-run."],
    });
  }

  const ctxBits = [];
  if (r.channel)       ctxBits.push(`channel=${r.channel}`);
  if (r.message_type)  ctxBits.push(`type=${r.message_type}`);
  if (r.voice_profile_source) ctxBits.push(`voice=${r.voice_profile_source}`);
  if (r.brief)         ctxBits.push(`brief="${truncate(r.brief, 60)}"`);

  if (!reasons.length) {
    // Last-resort: we got an empty variants[] with no diagnostics. The
    // most common cause is the LLM returning {"variants": []} (rare —
    // usually means the brief was ambiguous or the model refused).
    reasons.push({
      title: "The model returned no usable drafts",
      lines: [
        "OpenAI returned a response but with an empty variants list. Try:",
        "• Rephrase the brief to be more specific (action + audience + outcome).",
        "• Pick a single channel instead of \"All Channels\".",
        "• If you just changed tone/type, re-run — JSON-mode hiccups are transient.",
      ],
    });
  }

  return (
    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" p={4}>
      <HStack mb={2}>
        <Tag size="sm" colorScheme="orange"><TagLabel>0 variants</TagLabel></Tag>
        <Text fontSize="sm" fontWeight="600" color="orange.900">No variants generated — here's why</Text>
      </HStack>
      {ctxBits.length > 0 && (
        <Text fontSize="xs" color="orange.800" mb={3}>{ctxBits.join(" · ")}</Text>
      )}
      <VStack align="stretch" spacing={3}>
        {reasons.map((rs, i) => (
          <Box key={i}>
            <Text fontSize="xs" fontWeight="700" color="orange.900" mb={1}>{rs.title}</Text>
            <VStack align="stretch" spacing={1}>
              {rs.lines.map((ln, j) => (
                <Text key={j} fontSize="xs" color="gray.800" whiteSpace="pre-wrap">• {ln}</Text>
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function Result({ result, onUseVariant, onClose, openerChannel, rerun }) {
  const toast = useToast();

  // ─── Onboarding required (FEAT-0008) ───
  // When the tenant has no historical templates AND no saved voice
  // profile, the agent returns a `requires_onboarding` envelope
  // (questionnaire_schema + tenant context). Show the form instead
  // of variants; save then re-fire the run via the AIAgentModal's
  // injected `rerun` callback.
  if (result?.requires_onboarding) {
    return <VoiceOnboardingForm result={result} rerun={rerun} toast={toast} />;
  }

  const variants = result?.variants || [];
  if (!variants.length) return <EmptyVariantsExplanation result={result} />;

  // When the operator picked "All Channels" the gateway returns a
  // grouped result — render a header per channel and the variants below
  // it. Each variant still carries its own verdict / issues / CTA.
  const isMulti = result?.channel === "all" || result?.multi === true;
  const groups = isMulti
    ? (result.channels || [...new Set(variants.map((v) => v.channel))]).map((ch) => ({
        channel: ch,
        bucket:  result.by_channel?.[ch],
        variants: variants.filter((v) => v.channel === ch),
      }))
    : null;

  return (
    <VStack align="stretch" spacing={3}>
      <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="lg" p={3}>
        <HStack spacing={3} fontSize="xs" color="blue.800" flexWrap="wrap">
          <Tag size="sm" colorScheme="blue"><TagLabel>{variants.length} variants</TagLabel></Tag>
          <Text>channel: <strong>{isMulti ? `all (${groups.length})` : result.channel}</strong></Text>
          {result.message_type && <Text>type: <strong>{result.message_type}</strong></Text>}
          {result.voice_profile_source && (
            <Tag size="sm" colorScheme={
              result.voice_profile_source.includes("onboarding") ? "purple" : "teal"
            }>
              <TagLabel>voice: {result.voice_profile_source.replace(/_/g, " ")}</TagLabel>
            </Tag>
          )}
          <Text>brief: <strong>{truncate(result.brief, 50)}</strong></Text>
        </HStack>
      </Box>

      {isMulti && groups.map((g) => (
        <Box key={g.channel} border="1px solid" borderColor="gray.100" borderRadius="lg" overflow="hidden">
          <Box bg="gray.50" px={3} py={2} borderBottom="1px solid" borderColor="gray.100">
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Text fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="0.4px" color="gray.700">
                  {g.channel}
                </Text>
                <Tag size="sm" colorScheme={g.variants.length ? "blue" : "gray"}>
                  <TagLabel>{g.variants.length} {g.variants.length === 1 ? "variant" : "variants"}</TagLabel>
                </Tag>
              </HStack>
              {g.bucket && !g.bucket.ok && (
                <Tag size="sm" colorScheme="red"><TagLabel>error</TagLabel></Tag>
              )}
            </HStack>
            {g.bucket && !g.bucket.ok && g.bucket.error && (
              <Text fontSize="11px" color="red.600" mt={1}>{g.bucket.error}</Text>
            )}
          </Box>
          <VStack align="stretch" spacing={2} p={3}>
            {g.variants.length === 0 && (
              <Text fontSize="xs" color="gray.500">No variants generated for this channel.</Text>
            )}
            {g.variants.map((v, i) => (
              <VariantCard key={`${g.channel}-${i}`} v={v} index={i}
                           onUseVariant={onUseVariant} onClose={onClose} toast={toast}
                           isMulti={isMulti} openerChannel={openerChannel} />
            ))}
          </VStack>
        </Box>
      ))}

      {!isMulti && variants.map((v, i) => (
        <VariantCard key={i} v={v} index={i}
                     onUseVariant={onUseVariant} onClose={onClose} toast={toast}
                     isMulti={false} openerChannel={openerChannel} />
      ))}
    </VStack>
  );
}

/**
 * Single variant card — extracted so the All-Channels grouped view and
 * the per-channel view render variants identically.
 */
function VariantCard({ v, index, onUseVariant, onClose, toast, isMulti, openerChannel }) {
  const verdict = v.verdict || "clean";
  // In multi-channel mode the modal can only paste back into the
  // single field that opened it. "Use this" stays enabled when the
  // variant's channel matches (or no opener channel was passed);
  // otherwise we hide it and keep Copy available.
  const canUseHere = !isMulti
    || !openerChannel
    || !v.channel
    || v.channel === openerChannel;
  return (
    <Box border="1px solid" borderColor="gray.200"
         borderLeft="3px solid"
         borderLeftColor={verdict === "clean" ? "green.400" : verdict === "warnings_only" ? "yellow.400" : "red.400"}
         borderRadius="lg" bg={verdict === "blocked" ? "red.50" : "white"} p={4}>
      <HStack justify="space-between" mb={2} flexWrap="wrap">
        <Text fontWeight="500" fontSize="sm" color="gray.700">Variant {index + 1}</Text>
        <HStack spacing={2}>
          <Tag size="sm"><TagLabel>{v.char_count || 0} chars</TagLabel></Tag>
          {v.angle && <Tag size="sm"><TagLabel>{v.angle}</TagLabel></Tag>}
          <Badge colorScheme={VERDICT_COLOR[verdict] || "gray"}>{verdict.replace("_", " ")}</Badge>
        </HStack>
      </HStack>
      {/* Email components — subject + preheader + format are rendered
          as their own block above the body. */}
      {(v.components?.subject || v.components?.preheader || v.components?.format) && (
        <Box border="1px dashed" borderColor="gray.300" borderRadius="md" px={3} py={2} mb={2} bg="gray.50">
          {v.components?.subject && (
            <>
              <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px">Subject</Text>
              <Text fontSize="sm" color="gray.800">{v.components.subject}</Text>
            </>
          )}
          {v.components?.preheader && (
            <Box mt={2}>
              <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px">Preheader</Text>
              <Text fontSize="xs" color="gray.700">{v.components.preheader}</Text>
            </Box>
          )}
          {v.components?.format && (
            <HStack spacing={2} mt={2}>
              <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px">Format</Text>
              <Tag size="sm" colorScheme={
                v.components.format === "html_code" ? "purple"
                : v.components.format === "html" ? "blue"
                : "gray"
              }>
                <TagLabel>
                  {v.components.format === "html_code" ? "HTML Code (full document)"
                   : v.components.format === "html" ? "Rich Text (HTML)"
                   : "Plain Text"}
                </TagLabel>
              </Tag>
            </HStack>
          )}
        </Box>
      )}

      {/* Header — only for channels with rich components */}
      {v.components?.header && v.components.header.type && v.components.header.type !== "none" && (
        <Box border="1px dashed" borderColor="gray.300" borderRadius="md" px={3} py={2} mb={2} bg="gray.50">
          <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px">
            Header · {v.components.header.type}
          </Text>
          {v.components.header.type === "text" && v.components.header.content && (
            <Text fontSize="sm" color="gray.800" mt={1}>{v.components.header.content}</Text>
          )}
          {["image", "video", "document"].includes(v.components.header.type) && v.components.header.media_hint && (
            <Text fontSize="xs" color="gray.600" mt={1} fontStyle="italic">
              Asset hint: {v.components.header.media_hint}
            </Text>
          )}
          {v.components.header.type === "location" && (
            <Text fontSize="xs" color="gray.600" mt={1}>
              {v.components.header.location_name}{v.components.header.location_address ? " — " + v.components.header.location_address : ""}
            </Text>
          )}
        </Box>
      )}

      {/* Body */}
      <Box bg="gray.50" borderRadius="md" p={3} fontSize="sm" color="gray.800" mb={2} whiteSpace="pre-wrap">
        {v.text}
      </Box>

      {/* Footer */}
      {v.components?.footer && (
        <Box border="1px dashed" borderColor="gray.300" borderRadius="md" px={3} py={1.5} mb={2} bg="gray.50">
          <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px">Footer</Text>
          <Text fontSize="xs" color="gray.700">{v.components.footer}</Text>
        </Box>
      )}

      {/* Buttons — rendered as real CTA buttons (WhatsApp-style), with the
          custom-link URL / call phone shown below each button. */}
      {Array.isArray(v.components?.buttons) && v.components.buttons.length > 0 && (
        <Box mb={2}>
          <Text fontSize="11px" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.3px" mb={1.5}>
            Buttons
          </Text>
          <VStack align="stretch" spacing={2}>
            {v.components.buttons.map((btn, bi) => {
              const label = btn.type === "copy-offer" ? "Copy offer code" : (btn.text || "(no label)");
              const scheme = btn.type === "quick-reply" ? "purple"
                : btn.type === "custom-link" ? "blue"
                : btn.type === "call-me" || btn.type === "voice-call" ? "green"
                : btn.type === "copy-offer" ? "orange"
                : "gray";
              const icon = btn.type === "custom-link" ? <LuExternalLink size={14} />
                : btn.type === "call-me" || btn.type === "voice-call" ? <LuPhone size={14} />
                : btn.type === "copy-offer" ? <LuCopy size={14} />
                : <LuReply size={14} />;
              // custom-link → its URL; call buttons → the phone number.
              const sub = btn.type === "custom-link" ? btn.url
                : (btn.type === "call-me" || btn.type === "voice-call") ? btn.phone
                : null;
              return (
                <Box key={bi}>
                  <Button size="sm" w="full" variant="outline" colorScheme={scheme}
                          leftIcon={icon} justifyContent="center" fontWeight="600" borderRadius="md">
                    {label}
                  </Button>
                  {sub && (
                    <Text fontSize="10px" color="gray.500" mt={1} textAlign="center" noOfLines={1}>
                      {sub}
                    </Text>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}

      {v.cta && <Text fontSize="xs" color="gray.600">CTA: <strong>{v.cta}</strong></Text>}
      {v.rationale && <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">{v.rationale}</Text>}
      {(v.issues || []).map((iss, k) => (
        <Text key={k} fontSize="11px" color={iss.severity === "error" ? "red.600" : "yellow.700"} mt={1}>
          {iss.severity === "error" ? "✗" : "⚠"} <Code fontSize="11px">{iss.code}</Code>: {iss.message}
        </Text>
      ))}
      {verdict !== "blocked" && onUseVariant && (
        <Box mt={3}>
          <Divider mb={2} />
          <Flex justify="flex-end" align="center" gap={2}>
            {isMulti && !canUseHere && (
              <Text fontSize="11px" color="gray.500">
                opens {openerChannel}; copy + paste into the {v.channel} tab
              </Text>
            )}
            <Button size="xs" variant="ghost" onClick={() => { navigator.clipboard?.writeText(v.text); toast({ title: "Copied", status: "success", duration: 1200 }); }}>Copy</Button>
            {canUseHere && (
              <Button size="sm" colorScheme="blue"
                onClick={() => { onUseVariant(v.text, v); onClose(); }}>
                Use this
              </Button>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

function truncate(s, n) { if (!s) return ""; return s.length <= n ? s : s.slice(0, n - 1) + "…"; }

/**
 * Voice onboarding questionnaire (FEAT-0008).
 *
 * Rendered when the Copy Suggester returns `requires_onboarding: true`
 * — i.e. the tenant has no approved templates AND no saved voice
 * profile. Posts the questionnaire to
 * `POST /api/voice-profile/:customer_id`, then calls `rerun()` from
 * the parent AIAgentModal so generation proceeds with the just-saved
 * profile in place.
 */
function VoiceOnboardingForm({ result, rerun, toast }) {
  const schema = result.questionnaire_schema;
  const cid = result.customer_id;
  const [data, setData] = useState(() => initialFormState(schema));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const submit = async () => {
    setSaving(true); setSaveError(null);
    try {
      // Resolve gateway base URL via the same client the agent uses.
      const base = (window.__AGENTS_CONSOLE_URL__ || process.env.REACT_APP_AGENTS_CONSOLE_URL || "http://localhost:7800");
      const r = await fetch(`${base}/api/voice-profile/${cid}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(data),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || `Save failed (${r.status})`);
      if (j.redactions_applied) {
        toast({ title: "PII removed from your input", description: "We stripped email/phone/IP-looking values before saving.", status: "warning", duration: 4000 });
      } else {
        toast({ title: "Voice profile saved", status: "success", duration: 1500 });
      }
      // Re-fire the agent — now it'll find the saved profile.
      if (rerun) await rerun();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={4}>
        <Text fontWeight="600" color="purple.900" fontSize="sm" mb={1}>Onboarding — set up your brand voice</Text>
        <Text fontSize="xs" color="purple.800">
          {schema.intro || "Help the AI write copy that sounds like your brand. This takes ~1 minute and saves once per tenant."}
        </Text>
        {result?.tenant?.business_name && (
          <Text fontSize="11px" color="purple.700" mt={2}>
            tenant: <strong>{result.tenant.business_name}</strong>{result.tenant.industry ? ` · ${result.tenant.industry}` : ""}
          </Text>
        )}
      </Box>

      {schema.sections.map((section) => (
        <Box key={section.key} border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
          <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>{section.label}</Text>
          <VStack spacing={3} align="stretch">
            {section.fields.map((field) => (
              <QuestionnaireField key={field.key} section={section.key} field={field}
                value={data[section.key]?.[field.key]}
                onChange={(v) => setData((prev) => ({
                  ...prev,
                  [section.key]: { ...(prev[section.key] || {}), [field.key]: v }
                }))} />
            ))}
          </VStack>
        </Box>
      ))}

      {saveError && (
        <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
          <Text fontSize="sm" color="red.700">{saveError}</Text>
        </Box>
      )}

      <Flex justify="flex-end" gap={2}>
        <Button colorScheme="purple" onClick={submit} isLoading={saving} loadingText="Saving + generating">
          Save & Generate
        </Button>
      </Flex>
    </VStack>
  );
}

function initialFormState(schema) {
  const out = {};
  for (const section of schema.sections || []) {
    out[section.key] = {};
    for (const field of section.fields || []) {
      out[section.key][field.key] = field.type === "multi_select" ? []
                                  : field.type === "csv"          ? ""
                                  : field.repeat_max              ? [""]
                                  : "";
    }
  }
  return out;
}

function QuestionnaireField({ section, field, value, onChange }) {
  const { key, label, type, required, options, placeholder, maxLength, repeat_max, help } = field;
  const labelText = `${label}${required ? " *" : ""}`;
  const Help = () => help ? <Text fontSize="11px" color="gray.500" mt={1}>{help}</Text> : null;
  if (type === "select") {
    return (
      <FormControl isRequired={required}>
        <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
        <Select size="sm" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="—">
          {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </Select>
        <Help />
      </FormControl>
    );
  }
  if (type === "multi_select") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
    };
    return (
      <FormControl isRequired={required}>
        <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
        <Flex wrap="wrap" gap={2}>
          {options.map((o) => (
            <Tag key={o} size="sm" cursor="pointer"
                 variant={selected.includes(o) ? "solid" : "outline"}
                 colorScheme={selected.includes(o) ? "purple" : "gray"}
                 onClick={() => toggle(o)}>
              <TagLabel>{o.replace(/_/g, " ")}</TagLabel>
            </Tag>
          ))}
        </Flex>
        <Help />
      </FormControl>
    );
  }
  if (type === "csv") {
    return (
      <FormControl isRequired={required}>
        <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
        <Input size="sm" value={value || ""} maxLength={maxLength}
               onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <Text fontSize="11px" color="gray.500" mt={1}>{help || "Comma-separated."}</Text>
      </FormControl>
    );
  }
  if (type === "textarea" && repeat_max) {
    const arr = Array.isArray(value) ? value : [value || ""];
    const update = (idx, v) => {
      const next = [...arr]; next[idx] = v;
      onChange(next.filter((x, i) => x || i < repeat_max));
    };
    const add = () => onChange([...arr, ""]);
    return (
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
        <VStack spacing={2} align="stretch">
          {arr.slice(0, repeat_max).map((entry, idx) => (
            <textarea key={idx} value={entry || ""} maxLength={maxLength}
              onChange={(e) => update(idx, e.target.value)}
              placeholder={placeholder || "Paste an example message"}
              style={{ minHeight: 60, padding: 8, fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit" }} />
          ))}
          {arr.length < repeat_max && (
            <Button size="xs" variant="ghost" onClick={add} alignSelf="flex-start">+ another</Button>
          )}
        </VStack>
        <Help />
      </FormControl>
    );
  }
  if (type === "textarea") {
    return (
      <FormControl isRequired={required}>
        <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
        <textarea value={value || ""} maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          style={{ minHeight: 70, padding: 8, fontSize: 13, width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit" }} />
        <Help />
      </FormControl>
    );
  }
  // default: text input
  return (
    <FormControl isRequired={required}>
      <FormLabel fontSize="xs" mb={1}>{labelText}</FormLabel>
      <Input size="sm" value={value || ""} maxLength={maxLength}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      <Help />
    </FormControl>
  );
}
